import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export default function ScrollToTop() {
  const { pathname, search, hash } = useLocation();
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Determine a unique key for the current exact location
    const scrollKey = `scroll_pos_${pathname}${search}${hash}`;

    // For specific paths where tab navigation shouldn't restore scroll to the bottom arbitrarily
    // (like Admin Dashboard tabs), ignore saved position.
    // The issue occurs because tabs within the same parent page share the scroll container,
    // and storing scroll per ?tab= forces a jump when switching tabs or visiting a specific tab link directly.
    const shouldIgnoreSavedPos = pathname.startsWith('/admin') && search.includes('tab=');
    const savedPos = shouldIgnoreSavedPos ? null : localStorage.getItem(scrollKey);

    // Clear any pending scroll restorations
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const restoreScroll = () => {
      let wPos = 0, aPos = 0, pPos = 0;
      if (savedPos) {
        const parts = savedPos.split(',');
        wPos = parseInt(parts[0], 10) || 0;
        aPos = parseInt(parts[1], 10) || 0;
        pPos = parseInt(parts[2], 10) || 0;
      }

      window.scrollTo(0, wPos);

      const appContainer = document.getElementById('app-main-scroll-container');
      if (appContainer) {
        appContainer.scrollTo(0, aPos);
      }

      const publicContainer = document.getElementById('public-layout-scroll-container');
      if (publicContainer) {
        publicContainer.scrollTo(0, pPos);
      }
    };

    // Attempt restoration immediately, but also defer slightly to allow React rendering/mounting
    restoreScroll();
    timeoutRef.current = setTimeout(restoreScroll, 100);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [pathname, search, hash]);

  useEffect(() => {
    let scrollTimeout;

    const handleScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      // Debounce scroll saving to avoid heavy lifting on every scroll frame
      scrollTimeout = setTimeout(() => {
        const key = `scroll_pos_${window.location.pathname}${window.location.search}${window.location.hash}`;

        const wPos = window.scrollY || 0;

        const appContainer = document.getElementById('app-main-scroll-container');
        const aPos = appContainer ? appContainer.scrollTop : 0;

        const publicContainer = document.getElementById('public-layout-scroll-container');
        const pPos = publicContainer ? publicContainer.scrollTop : 0;

        localStorage.setItem(key, `${wPos},${aPos},${pPos}`);
      }, 100);
    };

    // Use capturing phase so we catch scroll events from any container, not just window
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, []);

  return null;
}
